import { getTimezoneOffset } from "date-fns-tz";

import { TimeZone, timeZones, timeZoneAliasToCanonical } from "./list";
import { partition } from "../array";

type BuildListOption = {
  noDefault?: boolean;
};

const getRegion = (timezone: TimeZone) =>
  timezone.tzCode.split("/").reverse().pop();

export const getLocalTimeZoneCode = (): string => {
  const raw = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZoneAliasToCanonical[raw] ?? raw;
};

// utc field ignores DST and goes stale
// sample both sides of the year
// skip a zone that changes clocks when the browser doesn't
export const findMatchingTimezone = (timezones: TimeZone[]) => {
  const now = new Date();
  const year = now.getFullYear();
  const dates = [now, new Date(year, 0, 1), new Date(year, 6, 1)];
  return timezones.find((tz) =>
    dates.every(
      (date) =>
        getTimezoneOffset(tz.tzCode, date) ===
        -date.getTimezoneOffset() * 60_000,
    ),
  );
};

export const findLocalTimezone = (timezones: TimeZone[]) => {
  const localTzCode = getLocalTimeZoneCode();
  return (
    timezones.find((tz) => tz.tzCode === localTzCode) ||
    findMatchingTimezone(timezones)
  );
};

export const buildTimeZoneList = (option?: BuildListOption): TimeZone[] => {
  const sortedTimeZones = [...timeZones].sort((a, b) =>
    a.label > b.label ? 1 : -1,
  );
  if (option?.noDefault) return sortedTimeZones;

  const localTzCode = getLocalTimeZoneCode();
  const local = findLocalTimezone(sortedTimeZones);
  if (!local) return sortedTimeZones;

  // Move the local time-zone and region to the top of the list
  const [localRegion, otherRegions] = partition(
    sortedTimeZones,
    (item) => getRegion(item) === getRegion(local),
  );
  const [localTimeZone, otherTimeZones] = partition(
    localRegion,
    (item) => item.tzCode === localTzCode,
  );

  return [...localTimeZone, ...otherTimeZones, ...otherRegions];
};

export const getLongestTimeSpanInMinutes = (
  availabilities: {
    day: number;
    fromTime: {
      hour: number;
      minute: number;
    };
    toTime: {
      hour: number;
      minute: number;
    };
    enabled: boolean;
  }[],
) => {
  let longestSpanMinutes = 0;

  availabilities.forEach((availability) => {
    if (!availability.enabled) return;

    const startMinutes =
      availability.fromTime.hour * 60 + availability.fromTime.minute;
    const endMinutes =
      availability.toTime.hour * 60 + availability.toTime.minute;

    const spanMinutes = endMinutes - startMinutes;

    if (spanMinutes > longestSpanMinutes) {
      longestSpanMinutes = spanMinutes;
    }
  });

  return longestSpanMinutes;
};
